// LIBRERIE/IMPORTS
import React, { useState, useRef, useMemo, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import Select, { MultiValue } from "react-select";
import ReactSlider from "react-slider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDoubleDown } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

// HOOKS
import { useGetAddresses } from "../../hooks/useGetAddresses";
import useGetAgencyTypologies from "../../hooks/searchForm/useGetAgencyTypologies";
import useGetAgencyContracts from "../../hooks/searchForm/useGetAgencyContracts";

// CONFIGURAZIONI E MAPPE
import appConfig, { AGENCY_ID } from "../../config/appConfig";
import { contractTranslations, getContractTranslation, LanguageCode } from "../../config/i18n/elementsMappingProperty";
import { getTipologiaTranslation } from "../../config/i18n/propertyTranslations";
import { getSlugFromId } from "../../config/mappings";

// COMPONENTI
import LoadingIndicator from "../Loaders/LoadingIndicator";

interface SearchProps { searchData?: SearchData; initialFilters?: any; }
interface SearchData { giardino?: boolean; terrazzo?: boolean; piscina?: boolean; riferimento?: string; }
interface OptionType { readonly value: number | string | null; readonly label: string; }
interface FrazioneOption extends OptionType { readonly value: string; readonly isDisabled?: boolean; }

const PropertySearch: React.FC<SearchProps> = ({ searchData = {}, initialFilters }) => {
    // --- Hooks e State ---
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
    const [selectedContractId, setSelectedContractId] = useState<number | null>(null);
    const [selectedTipologia, setSelectedTipologia] = useState<readonly OptionType[]>([]);
    const [selectedComune, setSelectedComune] = useState<readonly OptionType[]>([]);
    const [selectedProvincia, setSelectedProvincia] = useState<OptionType | null>(null);
    const [selectedFrazione, setSelectedFrazione] = useState<FrazioneOption[]>([]);
    const [camereMin, setCamereMin] = useState<number | "">("");
    const [bagniMin, setBagniMin] = useState<number | "">("");
    const [sqmtMin, setSqmtMin] = useState<number | "">("");
    const [sqmtMax, setSqmtMax] = useState<number | "">("");
    const [priceRange, setPriceRange] = useState<readonly [number, number]>([0, 2000000]);
    const [priceMin, setPriceMin] = useState<number | "">("");
    const [priceMax, setPriceMax] = useState<number | "">("");
    const [localSearchData, setLocalSearchData] = useState<SearchData>(searchData);
    const advancedSearchRef = useRef<HTMLDivElement>(null);
    const CAPOLUOGO_VALUE = "30000";

    // --- CARICAMENTO DATI ---------------------------------------------------------------------------------------------------------//
    const { data: addresses, loading: loadingAddresses, error: errorAddresses } = useGetAddresses(AGENCY_ID);

    // Dati Contratti Immobili
    const { contracts, loading: loadingContracts, error: errorContracts, getAgencyContracts } = useGetAgencyContracts({
        agencyId: AGENCY_ID,
        enabled: true // abilita la fetch automatica
    });
    useEffect(() => {
        getAgencyContracts();
        if (errorContracts) {
            console.error("Errore caricamento contratti agenzia:", errorContracts);
        }
    }, [getAgencyContracts, loadingContracts]);

    // Dati Tipologie Immobili
    const { typologies, loading: loadingTypologies, error: errorTypologies, getAgencyTypologies } = useGetAgencyTypologies({
        agencyId: AGENCY_ID,
        enabled: true // abilita la fetch automatica
    });
    useEffect(() => {
        getAgencyTypologies();
        if (errorTypologies) {
            console.error("Errore caricamento tipologie agenzia:", errorTypologies);
        }
    }, [getAgencyTypologies, loadingTypologies]);

    // --------------------------------------------------------------------------------------------------------------------------------//

    // --- SINCRONIZZAZIONE STATO CON URL ---
    useEffect(() => {
        if (!initialFilters || loadingAddresses) return;

        // Popola Contratto
        if (initialFilters.contract?.length > 0) {
            setSelectedContractId(initialFilters.contract[0]);
        }

        // Popola Tipologia
        if (initialFilters.type?.length > 0) {
            const initialTipologie = initialFilters.type.map((id: number) => ({
                value: id,
                label: getTipologiaTranslation(id, i18n.language as LanguageCode)
            }));
            setSelectedTipologia(initialTipologie);
        }

        // Popola Provincia
        if (initialFilters.provincia && addresses?.province) {
            const foundProv = addresses.province.find(p => p.slug === initialFilters.provincia || p.sigla === initialFilters.provincia);
            if (foundProv) {
                setSelectedProvincia({ value: foundProv.sigla, label: foundProv.nome ?? '' });
            }
        }

        // Popola Comune (dipende dalla provincia)
        if (initialFilters.comune?.length > 0 && addresses?.comuni) {
            const initialComuni = addresses.comuni
                .filter(c => initialFilters.comune.includes(c.slug))
                .map(c => ({ value: c.codiceIstatComuneAlfa, label: c.nomeIt ?? '' }));
            setSelectedComune(initialComuni);
        }
        
        // --- Popola tutti i filtri avanzati ---
        if (initialFilters.minRooms) setCamereMin(initialFilters.minRooms);
        if (initialFilters.minBathrooms) setBagniMin(initialFilters.minBathrooms);
        if (initialFilters.sqmtMin) setSqmtMin(initialFilters.sqmtMin);
        if (initialFilters.sqmtMax) setSqmtMax(initialFilters.sqmtMax);
        if (initialFilters.ref) setLocalSearchData(prev => ({ ...prev, riferimento: initialFilters.ref }));
        
        // Gestione Prezzo
        const minPrice = initialFilters.minPrice || 0;
        const maxPrice = initialFilters.maxPrice || 2000000;
        setPriceRange([minPrice, maxPrice]);

        // Gestione Checkbox
        setLocalSearchData(prev => ({
            ...prev,
            giardino: initialFilters.gardenPrivate || false,
            terrazzo: initialFilters.terrace || false,
            piscina: initialFilters.swimmingPool || false,
        }));

    }, [initialFilters, loadingAddresses, addresses, i18n.language]);


    // --- GENERAZIONE OPZIONI ---
    const allAgencyContracts: readonly OptionType[] = useMemo(() => {
        if (!contracts || contracts.length === 0) return [{ value: null, label: t('common.(any)') }];
        return [
            { value: null, label: t('common.(any)') },
            ...contracts.map(t => ({
                value: t.id,
                label: t.contratto ?? ''
            })).sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''))
        ]
    },[contracts, t]);

    const allTipologiesAgency: readonly OptionType[] = useMemo(() => {
        if (!typologies || typologies.length === 0) return [{ value: null, label: t('common.(any)') }];
        return [
            { value: null, label: t('common.(any)') },
            ...typologies.map(t => ({
                value: t.id,
                label: t.tipologia ?? ''
            })).sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''))
        ];
    }, [typologies, t]);

    const contratti: readonly OptionType[] = useMemo(() => [
        { value: null, label: t('common.(any)') },
        ...contractTranslations
            .map(c => ({ value: c.id, label: getContractTranslation(c.id, i18n.language as LanguageCode) }))
            .sort((a, b) => a.label.localeCompare(b.label))
    ], [i18n.language, t]);

    const provinceOptions: readonly OptionType[] = useMemo(() => [
        { value: null, label: t('common.(any)') },
        ...(addresses?.province.map(p => ({ value: p.sigla, label: p.nome ?? '' }))
            .sort((a, b) => a.label.localeCompare(b.label)) ?? [])
    ], [addresses, t]);

    const comuneOptions: readonly OptionType[] = useMemo(() => {
        if (!selectedProvincia?.value || !addresses?.comuni) return [{ value: null, label: t('common.(any)') }];
        return [
            { value: null, label: t('common.(any)') },
            ...addresses.comuni
                .filter(c => c.siglaProvincia === selectedProvincia.value)
                .map(c => ({ value: c.codiceIstatComuneAlfa, label: c.nomeIt ?? '' }))
                .sort((a, b) => a.label.localeCompare(b.label))
        ];
    }, [selectedProvincia, addresses, t]);

    // --- Funzioni Helper ---
    const isCapoluogoSelected = (): boolean => {
        return selectedFrazione.some(frazione => frazione.value === CAPOLUOGO_VALUE);
    };

    // Opzioni Frazioni (filtrate per comune/i selezionato/i)
    const getFrazioneOptions = (): readonly FrazioneOption[] => {
        const showCapoluogoOption = selectedComune.length > 0; // Mostra CAPOLUOGO se almeno un comune è selezionato

        const frazioniBase: FrazioneOption[] = (selectedComune.length > 0 && addresses?.frazioni // Calcola solo se comuni selezionati
            ? addresses.frazioni
                .filter((frazione) => selectedComune.some(c => c.value === frazione.comuneIstat)) // Filtra per i comuni selezionati
                .map((frazione) => ({
                    value: frazione.id?.toString() ?? "", // Assicura sia stringa
                    label: frazione.frazione ?? t('common.notAvailable'),
                    isDisabled: false,
                }))
                .sort((a, b) => a.label.localeCompare(b.label))
            : []) ?? [];

        const options: FrazioneOption[] = [
            ...(showCapoluogoOption ? [{ value: CAPOLUOGO_VALUE, label: t('searchWidget.capoluogoLabel'), isDisabled: false }] : []),
            ...frazioniBase,
        ];

        // Logica per disabilitare opzioni
        const disableAll = selectedComune.length > 1; // Disabilita se più comuni selezionati
        const disableNonCapoluogo = isCapoluogoSelected(); // Disabilita altre se CAPOLUOGO selezionato

        return options.map(frazione => ({
            ...frazione,
            isDisabled: frazione.isDisabled || disableAll || (disableNonCapoluogo && frazione.value !== CAPOLUOGO_VALUE)
        }));
    };

    // --- Handlers Eventi ---
    // Handler per il toggle della ricerca avanzata
    const toggleAdvancedSearch = (event: React.MouseEvent) => {
        event.preventDefault();
        setIsAdvancedSearchOpen(prev => !prev);
    };

    const handleContractChange = (selectedOption: OptionType | null) => {
        // Imposta solo l'ID nel tuo stato
        setSelectedContractId(selectedOption?.value as number | null ?? null);
    };

    // Handlers per i Select (usano OptionType generico)
    const handleTipologiaChange = (newValue: MultiValue<OptionType>) => {
        setSelectedTipologia(newValue as readonly OptionType[]);
    };

    const handleProvinciaChange = (selectedOption: OptionType | null) => {
        setSelectedProvincia(selectedOption);
        setSelectedComune([]);
        setSelectedFrazione([]);
    };

    const handleComuneChange = (newValue: MultiValue<OptionType>) => {
        setSelectedComune(newValue as readonly OptionType[]);
        setSelectedFrazione([]); // Resetta frazione
    };

    // Handler specifico per Frazioni che gestisce la conversione a FrazioneOption
    const handleFrazioneChange = (newValue: MultiValue<OptionType>) => {
        const newFrazioneValues = (Array.isArray(newValue) ? newValue : [])
            .filter(option => option.value !== null && typeof option.value === 'string')
            .map(option => ({
                value: option.value as string,
                label: option.label,
                isDisabled: getFrazioneOptions().find(fo => fo.value === option.value)?.isDisabled ?? false
            })) as FrazioneOption[];

        // Logica per selezionare solo CAPOLUOGO se scelto
        if (newFrazioneValues.some(frazione => frazione.value === CAPOLUOGO_VALUE)) {
            setSelectedFrazione(newFrazioneValues.filter(frazione => frazione.value === CAPOLUOGO_VALUE));
        } else {
            setSelectedFrazione(newFrazioneValues);
        }
    };

    // Handlers per input numerici
    const handleNumericInputChange = (
        setter: React.Dispatch<React.SetStateAction<number | "">>
    ) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setter(value === "" ? "" : parseInt(value, 10) || 0); // Converte in numero, fallback a 0 se NaN
    };

    const handleCamereMinChange = handleNumericInputChange(setCamereMin);
    const handleBagniMinChange = handleNumericInputChange(setBagniMin);
    const handleSqmtMinChange = handleNumericInputChange(setSqmtMin);
    const handleSqmtMaxChange = handleNumericInputChange(setSqmtMax);

    // Handler per lo Slider price
    const handlePriceChange = (values: number | readonly number[]) => {
        if (Array.isArray(values) && values.length === 2
            && typeof values[0] === 'number' && typeof values[1] === 'number') {
            setPriceRange(values as unknown as readonly [number, number]);
        }
    };
    // Handler per lo Input Number price
    const handlePriceMinChange = handleNumericInputChange(setPriceMin);
    const handlePriceMaxChange = handleNumericInputChange(setPriceMax);

    // Handler per il campo Riferimento
    const handleRiferimentoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalSearchData(prev => ({ ...prev, riferimento: event.target.value }));
    };

    // Handler per le Checkbox (aggiorna localSearchData)
    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target;
        setLocalSearchData(prev => ({ ...prev, [name as keyof SearchData]: checked }));
    };

    // --- SUBMIT FORM ---
    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();

        const urlSegments: string[] = [];
        const queryParams = new URLSearchParams();

        const contractSlug = selectedContractId ? getSlugFromId('contratto', selectedContractId) : null;
        if (contractSlug) urlSegments.push(contractSlug);

        if (selectedTipologia.length === 1 && selectedTipologia[0].value) {
            const tipologiaSlug = getSlugFromId('tipologia', Number(selectedTipologia[0].value));
            if (tipologiaSlug) urlSegments.push(tipologiaSlug);
        }

        if (selectedProvincia?.value && addresses) {
            const provData = addresses.province.find(p => p.sigla === selectedProvincia.value);
            if (provData?.slug) urlSegments.push(provData.slug);

            if (selectedComune.length === 1 && selectedComune[0].value) {
                const comData = addresses.comuni.find(c => c.codiceIstatComuneAlfa === selectedComune[0].value);
                if (comData?.slug) urlSegments.push(comData.slug);

                if (selectedFrazione.length === 1 && selectedFrazione[0].value !== CAPOLUOGO_VALUE) {
                    const frazData = addresses.frazioni.find(f => String(f.id) === selectedFrazione[0].value);
                    if (frazData?.slug) urlSegments.push(frazData.slug);
                }
            }
        }
        
        const path = `/immobili/${urlSegments.join('/')}`;

        if (selectedTipologia.length > 1) selectedTipologia.forEach(t => t.value && queryParams.append('tipologia', String(t.value)));
        if (selectedComune.length > 1) selectedComune.forEach(c => c.value && queryParams.append('comune', String(c.value)));
        if (isCapoluogoSelected()) { 
            queryParams.set('frazione', CAPOLUOGO_VALUE);
        }else if (selectedFrazione.length > 1) { 
            selectedFrazione.forEach(f => f.value && queryParams.append('frazione', f.value)); 
        }
        if (camereMin !== "") queryParams.set('camere_min', String(camereMin));
        if (bagniMin !== "") queryParams.set('bagni_min', String(bagniMin));
        if (sqmtMin !== "") queryParams.set('mq_min', String(sqmtMin));
        if (sqmtMax !== "") queryParams.set('mq_max', String(sqmtMax));

        // Range Prezzo (solo se diversi dai default)
        if (appConfig.ui.searchForm.showPriceSlider) {
            if (priceRange[0] > 0) queryParams.set('prezzo_min', String(priceRange[0]));
            if (priceRange[1] < 2000000) queryParams.set('prezzo_max', String(priceRange[1]));
        } else {
            if (priceMin > "0") queryParams.set('prezzo_min', String(priceMin));
            if (priceMax > "0") queryParams.set('prezzo_max', String(priceMax));
        }

        if (localSearchData.riferimento) queryParams.set('riferimento', localSearchData.riferimento);
        if (localSearchData.giardino) queryParams.set('giardino_privato', 'true');
        if (localSearchData.terrazzo) queryParams.set('terrazzo', 'true');
        if (localSearchData.piscina) queryParams.set('piscina', 'true');

        const queryString = queryParams.toString();

        console.log(`${path}${queryString ? '?' + queryString : ''}`)
        navigate(`${path}${queryString ? '?' + queryString : ''}`);
    };

    // --- RENDERING ---
    if (loadingAddresses) return <LoadingIndicator />;
    if (errorAddresses) {
        console.error("Errore caricamento dati ricerca:", errorAddresses);
        return <div className="alert alert-danger">{t('validation.loadingError')}</div>;
    }

    // --- Variabili per Traduzioni Comuni (per pulizia JSX) ---
    const labelContract = t('searchWidget.contract');
    const labelType = t('searchWidget.type');
    const labelProvince = t('searchWidget.province');
    const labelCity = t('searchWidget.city');
    const labelFraction = t('searchWidget.fraction');
    const labelRooms = t('common.rooms');
    const labelBathrooms = t('common.bathrooms');
    const labelPrice = t('common.price');
    const labelPriceMin = t('common.priceMin');
    const labelPriceMax = t('common.priceMax');
    const labelSqmtMin = t('property.sqmtMin');
    const labelSqmtMax = t('property.sqmtMax');
    const labelReference = t('property.referenceExtended');
    const labelOther = t('common.other');
    const labelGarden = t('property.garden.label');
    const labelTerrace = t('property.terrace');
    const labelPool = t('property.pool');
    const labelSearch = t('common.search');
    const labelAdvancedSearch = t('searchWidget.advancedSearchTitle');
    const placeholderAny = t('common.(any)');
    const placeholderType = t('searchWidget.type');
    const placeholderCity = t('searchWidget.city');
    const placeholderFraction = t('searchWidget.fraction');
    const placeholderRoomsMin = t('searchWidget.fieldMin', { field: labelRooms });
    const placeholderBathroomsMin = t('searchWidget.fieldMin', { field: labelBathrooms });
    const placeholderPriceMin = t('searchWidget.fieldMin', { field: labelPrice });
    const placeholderPriceMax = t('searchWidget.fieldMax', { field: labelPrice });
    const msgNoResults = t('common.noResults', 'Nessun risultato');
    const msgSelectProvinceFirst = t('searchWidget.selectProvinceFirst', 'Seleziona prima una provincia');
    const msgSelectOneCityFirst = t('searchWidget.selectOneCityFirst', 'Seleziona un solo comune');

    const currentContractOption = contratti.find(c => c.value === selectedContractId) || null;

    //Stile select
    const customStyles  = {
        control: (base: any, state: any) => ({
            ...base,
            border: state.isFocused ?"1px solid #07bd65 !important": "",    
            boxShadow: "none",
            minHeight: "45px",
        }),
        placeholder: (base: any) => ({
            ...base,
            color: "#000",
        }),
        menu: (base:any) => ({
            ...base,      
            overflowY: "auto",
            zIndex: "10" ,
            scrollbarWidth: "none" 
        }),
        menuList: (base:any) => ({
            ...base,
            maxHeight:"100px",
        }),
        option: (base:any, state: any) => ({
            ...base,
            backgroundColor: state.isFocused? "#eee" : "",
            color: state.isSelected? "#000": "#000",
            fontWeight: state.isSelected? "bold": "",
        })
    }

    return (
        <div className="property-search-field">
            <div className="property-search-item">

                <form
                    id="search"
                    className="form-row basic-select-wrapper align-items-end"
                    onSubmit={handleSubmit}
                    noValidate
                >

                    {/* Contratto */}
                    <div className="form-group col-12 col-md-6 col-lg">
                        <label htmlFor="select_contratto">{labelContract}</label>
                        <Select<OptionType, false>
                            inputId="select_contratto"
                            name="contratto"
                            options={allAgencyContracts}
                            value={currentContractOption} // Usa lo stato basato sull'ID
                            onChange={handleContractChange}
                            placeholder={placeholderAny}
                            classNamePrefix="react-select"
                            menuPortalTarget={document.body}
                            styles={customStyles}
                            aria-label={labelContract ?? ''}
                        />
                    </div>

                    {/* Tipologia */}
                    <div className="form-group col-12 col-md-6 col-lg">
                        <label htmlFor="select_tipologia">{labelType}</label>
                        <Select<OptionType, true>
                            inputId="select_tipologia"
                            name="tipologia"
                            isMulti
                            options={allTipologiesAgency}
                            value={selectedTipologia}
                            onChange={handleTipologiaChange}
                            placeholder={placeholderType}
                            closeMenuOnSelect={false}
                            classNamePrefix="react-select"
                            menuPortalTarget={document.body}
                            styles={ customStyles }
                            aria-label={labelType ?? ''}
                        />
                    </div>

                    {/* Provincia */}
                    <div className="form-group col-12 col-md-6 col-lg">
                        <label htmlFor="select_provincia">{labelProvince}</label>
                        <Select<OptionType, false>
                            inputId="select_provincia"
                            name="provincia"
                            value={selectedProvincia}
                            onChange={handleProvinciaChange}
                            options={provinceOptions}
                            placeholder={placeholderAny}
                            classNamePrefix="react-select"
                            menuPortalTarget={document.body}
                            styles={ customStyles }
                            aria-label={labelProvince ?? ''}
                        />
                    </div>

                    {/* Comune */}
                    <div className="form-group col-12 col-md-6 col-lg">
                        <label htmlFor="select_comune">{labelCity}</label>
                        <Select<OptionType, true>
                            inputId="select_comune"
                            name="comune"
                            isMulti
                            value={selectedComune}
                            onChange={handleComuneChange}
                            options={comuneOptions}
                            isDisabled={!selectedProvincia?.value} // Disabilita se provincia non selezionata
                            placeholder={placeholderCity}
                            closeMenuOnSelect={false}
                            classNamePrefix="react-select"
                            menuPortalTarget={document.body}
                            styles={customStyles}
                            aria-label={labelCity ?? ''}
                            noOptionsMessage={() => selectedProvincia?.value ? msgNoResults : msgSelectProvinceFirst}
                        />
                    </div>

                    {/* Bottone Cerca Desktop */}
                    <div id="btn-pc" className="form-group col-12 col-md-6 col-lg">
                        <span className="d-none d-lg-flex justify-content-center">
                            <button className="btn btn-primary w-75" type="submit" >
                                <span>{labelSearch}</span>
                            </button>
                        </span>
                    </div>

                    {/* Toggle Ricerca Avanzata */}
                    <div className="advanced-button">
                        <a
                            className={`more-search p-0 ${isAdvancedSearchOpen ? "open" : ""}`}
                            href="#advanced-search"
                            title={labelAdvancedSearch ?? ''}
                            role="button"
                            aria-expanded={isAdvancedSearchOpen}
                            aria-controls="advanced-search"
                            onClick={toggleAdvancedSearch}
                        >
                            <i>
                                <FontAwesomeIcon icon={faAngleDoubleDown} />
                            </i>
                        </a>
                    </div>

                    {/* Sezione Ricerca Avanzata */}
                    <div
                        className={`advanced-search col-12 ${isAdvancedSearchOpen ? "show" : ""}`}
                        id="advanced-search"
                        ref={advancedSearchRef}
                    >
                        <div className="form-row card">

                            {/* Frazione */}
                            <div className="form-group col-12 col-md-6 col-lg">
                                <label htmlFor="select_frazione">{labelFraction}</label>
                                <Select<FrazioneOption, true>
                                    inputId="select_frazione"
                                    name="frazione"
                                    isMulti
                                    value={selectedFrazione}
                                    onChange={handleFrazioneChange}
                                    options={getFrazioneOptions()}
                                    isDisabled={!selectedProvincia?.value || selectedComune.length === 0 || selectedComune.length > 1}
                                    placeholder={placeholderFraction}
                                    closeMenuOnSelect={false}
                                    classNamePrefix="react-select"
                                    menuPortalTarget={document.body}
                                    styles={customStyles}
                                    aria-label={labelFraction ?? ''}
                                    noOptionsMessage={() => !selectedProvincia?.value || selectedComune.length === 0 ? msgSelectOneCityFirst : msgNoResults}
                                />
                            </div>

                            {/* Camere Min */}
                            <div className="form-group col-12 col-md-6 col-lg">
                                <label htmlFor="input_camere_min">{placeholderRoomsMin}</label>
                                <input
                                    type="number"
                                    id="input_camere_min"
                                    name="camere_min"
                                    className="form-control"
                                    min="0"
                                    step="1"
                                    value={camereMin}
                                    onChange={handleCamereMinChange}
                                    placeholder={labelRooms}
                                    aria-label={placeholderRoomsMin ?? ''}
                                />
                            </div>

                            {/* Bagni Max */}
                            <div className="form-group col-12 col-md-6 col-lg">
                                <label htmlFor="input_bagni_min">{placeholderBathroomsMin}</label>
                                <input
                                    type="number"
                                    id="input_bagni_min"
                                    name="bagni_min"
                                    className="form-control"
                                    min="0"
                                    step="1"
                                    value={bagniMin}
                                    onChange={handleBagniMinChange}
                                    placeholder={labelBathrooms}
                                    aria-label={placeholderBathroomsMin ?? ''}
                                />
                            </div>

                            {/* In base a showPriceSlider attiva / disattiva l'input range per il prezzo */}
                            {/* Prezzo Range Slider */}
                            {appConfig.ui.searchForm.showPriceSlider ? (
                                <div className="form-group property-price-slider col-12 col-md-6 col-lg-5">
                                    <label id="price-range-label">{labelPrice}</label>
                                    <div className="slider-container">
                                        {/* Etichette nascoste per accessibilità slider */}
                                        <span id="price-min-label" style={{ display: 'none' }}>{t('property.minPriceLabel', 'Prezzo minimo')}</span>
                                        <span id="price-max-label" style={{ display: 'none' }}>{t('property.maxPriceLabel', 'Prezzo massimo')}</span>
                                        <ReactSlider
                                            min={0}
                                            max={2000000}
                                            step={10000}
                                            onChange={handlePriceChange}
                                            value={priceRange}
                                            className="price-range-slider"
                                            thumbClassName="slider-thumb"
                                            trackClassName="slider-track"
                                            ariaLabelledby={['price-min-label', 'price-max-label']}
                                            renderTrack={(props, state) => {

                                                const trackClass = state.index === 1 ? 'slider-track selected' : 'slider-track';
                                                return <div {...props} className={trackClass} />;
                                            }}
                                        />
                                        <div className="d-flex justify-content-between slider-prices">
                                            <span className="price-label left">{new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(priceRange[0])}</span>
                                            <span className="price-label right">{new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(priceRange[1])}</span>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {/*Prezzo minimo */}
                                    <div className="form-group col-12 col-md-6 col-lg">
                                        <label htmlFor="input_price_min">{placeholderPriceMin}</label>
                                        <input
                                            type="number"
                                            id="input_price_min"
                                            name="price_min"
                                            className="form-control"
                                            min="0"
                                            step="10000"
                                            value={priceMin}
                                            onChange={handlePriceMinChange}
                                            placeholder={labelPriceMin}
                                            aria-label={placeholderPriceMin ?? ''}
                                        />
                                    </div>

                                    {/*Prezzo max */}
                                    <div className="form-group col-12 col-md-6 col-lg">
                                        <label htmlFor="input_price_min">{placeholderPriceMax}</label>
                                        <input
                                            type="number"
                                            id="input_price_max"
                                            name="price_max"
                                            className="form-control"
                                            max="2000000"
                                            step="10000"
                                            value={priceMax}
                                            onChange={handlePriceMaxChange}
                                            placeholder={labelPriceMax}
                                            aria-label={placeholderPriceMax ?? ''}
                                        />
                                    </div>
                                </>
                            )}
                            {/* Inputs nascosti non sono più strettamente necessari se si usa handleSubmit */}
                            {/* <input type="hidden" name="prezzo_min" value={priceRange[0]} /> */}
                            {/* <input type="hidden" name="prezzo_max" value={priceRange[1]} /> */}
                        
                            <div className="d-none d-lg-block col-lg-12"></div>

                            {/* Mq Min */}
                            <div className="form-group col-12 col-md-6 col-lg">
                                <label htmlFor="input_mq_min">{labelSqmtMin}</label>
                                <input
                                    type="number"
                                    id="input_mq_min"
                                    name="mq_min"
                                    className="form-control"
                                    min="0"
                                    step="10"
                                    value={sqmtMin}
                                    onChange={handleSqmtMinChange}
                                    placeholder={labelSqmtMin ?? ''}
                                    aria-label={labelSqmtMin ?? ''}
                                />
                            </div>

                            {/* Mq Max */}
                            <div className="form-group col-12 col-md-6 col-lg">
                                <label htmlFor="input_mq_max">{labelSqmtMax}</label>
                                <input
                                    type="number"
                                    id="input_mq_max"
                                    name="mq_max"
                                    className="form-control"
                                    min="0"
                                    step="10"
                                    value={sqmtMax}
                                    onChange={handleSqmtMaxChange}
                                    placeholder={labelSqmtMax ?? ''}
                                    aria-label={labelSqmtMax ?? ''}
                                />
                            </div>

                            {/* Riferimento */}
                            <div className="form-group col-12 col-md-6 col-lg">
                                <label htmlFor="input_riferimento">{labelReference}</label>
                                <input
                                    type="text"
                                    id="input_riferimento"
                                    name="riferimento"
                                    value={localSearchData.riferimento || ""}
                                    onChange={handleRiferimentoChange}
                                    className="form-control"
                                    placeholder={labelReference ?? ''}
                                    aria-label={labelReference ?? ''}
                                />
                            </div>

                            {/* Altro (Checkboxes) */}
                            <div className="form-group col-12 col-lg-auto check">
                                <div className="form-group-search">
                                    <label>{labelOther}</label>
                                    <div className="d-flex pt-2">

                                        {/* Giardino */}
                                        <div className="form-check form-switch col-lg custom-control custom-checkbox">
                                            <input
                                                type="checkbox"
                                                id="giardino" // ID semplice
                                                name="giardino" // Corrisponde a chiave in SearchData
                                                className="custom-control-input"
                                                checked={!!localSearchData.giardino} // Usa !! per cast a booleano
                                                onChange={handleCheckboxChange}
                                            />
                                            <label className="custom-control-label" htmlFor="giardino">{labelGarden}</label>
                                        </div>

                                        {/* Terrazzo */}
                                        <div className="form-check form-switch col-lg custom-control custom-checkbox">
                                            <input
                                                type="checkbox"
                                                id="terrazzo"
                                                name="terrazzo"
                                                className="custom-control-input"
                                                checked={!!localSearchData.terrazzo}
                                                onChange={handleCheckboxChange}
                                            />
                                            <label className="custom-control-label" htmlFor="terrazzo">{labelTerrace}</label>
                                        </div>

                                        {/* Piscina */}
                                        <div className="form-check form-switch col-lg custom-control custom-checkbox">
                                            <input
                                                type="checkbox"
                                                id="piscina"
                                                name="piscina"
                                                className="custom-control-input"
                                                checked={!!localSearchData.piscina}
                                                onChange={handleCheckboxChange}
                                            />
                                            <label className="custom-control-label" htmlFor="piscina">{labelPool}</label>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* --- Bottone Cerca Mobile --- */}
                    <div id="btn-mobile" className={`d-lg-none btn-block btn-mobile m-3 mb-30imp ${isAdvancedSearchOpen ? 'order-last' : ''}`}>
                        <button className="btn btn-primary btn-block" type="submit">
                            <span>{labelSearch}</span>
                        </button>
                    </div>

                </form>

            </div >
        </div >
    );
};

export default PropertySearch;